import { Box } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { LabelText, ValueText } from '~/components/Fields/LabeledValue';
import ReviewStatus from '~/components/Structures/ReviewStatus';
import { RoiReadModel } from '~/domain/readmodels/StructureSetReadModel';

interface StructureDetailsInformationProps {
    roi: RoiReadModel;
    /** ROI volume in cm³ (already converted at the UI boundary). */
    volumeCm3: number;
    equivalentSphereDiameterCm: number;
}

/**
 * Read-only "Properties" section of the (i) Details popover: the full set of
 * identifying and physical properties for a structure — name, ROI number,
 * approval status, interpreted type, generation algorithm, volume,
 * equivalent-sphere diameter, contour count, electron/mass density, and
 * description. Laid out as a two-column grid (label, value) so labels and
 * values line up and the value sits close to its label. Volume is shown in cm³
 * and the equivalent-sphere diameter in cm (UI units); densities use 4-decimal
 * formatting and treat a missing override (not a legitimate zero) as an
 * em-dash. Only rendered for volumetric ROIs.
 */
export default function StructureDetailsInformation({
    roi,
    volumeCm3,
    equivalentSphereDiameterCm,
}: StructureDetailsInformationProps) {
    return (
        <Box
            display="grid"
            gridTemplateColumns="auto 1fr"
            gap="3px 6px"
            alignItems="baseline"
            sx={{
                '& .MuiTypography-root': {
                    fontSize: 11,
                    lineHeight: 1.2,
                },
            }}
        >
            <LabelText>
                <FormattedMessage id="name" />
            </LabelText>
            <ValueText
                component="span"
                title={roi.name}
                data-testid="structure-properties-name-value"
            >
                {roi.name}
            </ValueText>

            <LabelText>
                <FormattedMessage id="structure.roiNumber" />
            </LabelText>
            <ValueText
                component="span"
                data-testid="structure-properties-roi-number-value"
            >
                {roi.roiNumber}
            </ValueText>

            <LabelText>
                <FormattedMessage id="approvalStatus" />
            </LabelText>
            <ReviewStatus
                status={roi.approvalStatus}
                fontSize={11}
                color="text.primary"
                dataTestId="structure-properties-approval-value"
            />

            <LabelText>
                <FormattedMessage id="structure.type" />
            </LabelText>
            <ValueText
                component="span"
                data-testid="structure-properties-type-value"
            >
                {roi.interpretedType || '—'}
            </ValueText>

            <LabelText>
                <FormattedMessage id="structure.algorithm" />
            </LabelText>
            <ValueText
                component="span"
                data-testid="structure-properties-algorithm-value"
            >
                {roi.roiGenerationAlgorithm || '—'}
            </ValueText>

            <LabelText>
                <FormattedMessage id="structure.volume" />
            </LabelText>
            <ValueText
                component="span"
                data-testid="structure-properties-volume-value"
            >
                {isFinite(volumeCm3) ? `${volumeCm3.toFixed(2)} cm³` : '—'}
            </ValueText>

            <LabelText>
                <FormattedMessage id="structure.eqSphereDiam" />
            </LabelText>
            <ValueText component="span">
                {isFinite(equivalentSphereDiameterCm)
                    ? `${equivalentSphereDiameterCm.toFixed(2)} cm`
                    : '—'}
            </ValueText>

            <LabelText>
                <FormattedMessage id="structure.contours" />
            </LabelText>
            <ValueText component="span">{roi.contourCount}</ValueText>

            <LabelText>
                <FormattedMessage id="structure.electronDensity" />
            </LabelText>
            <ValueText
                component="span"
                data-testid="structure-electron-density-value"
            >
                {roi.electronDensityOverride != null
                    ? roi.electronDensityOverride.toFixed(4)
                    : '—'}
            </ValueText>

            <LabelText>
                <FormattedMessage id="structure.massDensity" />
            </LabelText>
            <ValueText
                component="span"
                data-testid="structure-mass-density-value"
            >
                {roi.massDensityOverride != null
                    ? roi.massDensityOverride.toFixed(4)
                    : '—'}
            </ValueText>

            {roi.roiDescription && (
                <>
                    <LabelText>
                        <FormattedMessage id="description" />
                    </LabelText>
                    <ValueText
                        component="span"
                        title={roi.roiDescription}
                        sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
                    >
                        {roi.roiDescription}
                    </ValueText>
                </>
            )}
        </Box>
    );
}
